import { useId, useState } from 'react';
import { Button } from '../../../../components/Button';
import { SelectPopover } from '../../../../components/SelectPopover';
import type { JucoYear } from '../../../../utils/roster-class';
import {
  JUCO_YEAR_LABELS,
  jucoYearForRow,
  jucoYearFromLabel,
  jucoYearLabel,
  unresolvedJucoRows,
  type JucoReviewRow,
  type JucoYearEdits,
} from './jucoImport';
import './JucoYearReview.css';

interface JucoYearReviewProps {
  /**
   * Distinguishes one review from another on the same page. A showcase batch shows one of these
   * per team card, and every control id inside has to stay unique across them.
   */
  idPrefix: string;
  /** The team's rows, in roster order. */
  rows: JucoReviewRow[];
  /** JC years the coach has filled in, keyed by row index. */
  edits: JucoYearEdits;
  /** Set (or clear, with null) one row's JC year. */
  onChangeRow: (rowIndex: number, year: JucoYear | null) => void;
  /** Set every still-unresolved row to one year. */
  onFillBlanks: (year: JucoYear) => void;
  disabled?: boolean;
}

/**
 * Per-row JC year review for a team the coach has marked JUCO.
 *
 * Most rows arrive already answered — the roster's Class column says `RS Fr.` and that reads as
 * JC 2 — so this list is mostly confirmation. It exists for the rows that do not: a `Jr` on a
 * two-year roster is suspect data the parser deliberately refuses to guess at, and two of the 41
 * pages in the scout book have no class column at all. Those rows block the import until someone
 * answers them, which is why the bulk fill above the list is not optional: without it a page with
 * no class column is an import with no way forward.
 *
 * The heading is a `<span>` inside `role="group"` rather than a `<label>`, because it names the
 * whole set of pickers rather than any one of them (see web/CLAUDE.md, Accessibility).
 */
export function JucoYearReview({
  idPrefix,
  rows,
  edits,
  onChangeRow,
  onFillBlanks,
  disabled = false,
}: JucoYearReviewProps) {
  const reactId = useId();
  const headingId = `${idPrefix}-juco-heading-${reactId}`;
  const bulkSelectId = `${idPrefix}-juco-bulk-${reactId}`;

  // The year the bulk control is armed with. Local because it is not part of the import — only
  // pressing Apply writes anything, so an idle pick cannot silently restate 40 players' years.
  const [bulkLabel, setBulkLabel] = useState('');

  const unresolved = unresolvedJucoRows(rows, edits);
  const bulkYear = jucoYearFromLabel(bulkLabel);

  const handleFillBlanks = () => {
    if (!bulkYear) return;
    onFillBlanks(bulkYear);
    setBulkLabel('');
  };

  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="juco-review" role="group" aria-labelledby={headingId}>
      <div className="juco-review__header">
        <span className="juco-review__heading" id={headingId}>
          JC year
        </span>
        <span className="juco-review__count" data-testid={`${idPrefix}-juco-unresolved-count`}>
          {unresolved.length === 0
            ? 'All rows have a JC year'
            : `${unresolved.length} of ${rows.length} need a JC year`}
        </span>
      </div>

      {unresolved.length > 0 && (
        <div className="juco-review__bulk">
          <label className="juco-review__bulk-label" htmlFor={bulkSelectId}>
            Set the {unresolved.length} unanswered {unresolved.length === 1 ? 'row' : 'rows'} to
          </label>
          <SelectPopover
            id={bulkSelectId}
            testId={`${idPrefix}-juco-bulk-select`}
            options={JUCO_YEAR_LABELS}
            value={bulkLabel}
            onChange={setBulkLabel}
            placeholder="Pick a JC year"
            clearable={false}
            disabled={disabled}
          />
          <Button
            variant="secondary"
            onClick={handleFillBlanks}
            disabled={disabled || !bulkYear}
            data-testid={`${idPrefix}-juco-bulk-apply`}
          >
            Apply
          </Button>
        </div>
      )}

      <ul className="juco-review__rows">
        {rows.map((row) => {
          const year = jucoYearForRow(row, edits);
          const rowSelectId = `${idPrefix}-juco-row-${row.index}-${reactId}`;

          return (
            <li
              key={row.index}
              className={`juco-review__row${year ? '' : ' juco-review__row--unresolved'}`}
            >
              <span className="juco-review__name">{row.label}</span>
              <span
                className={`juco-review__chip${row.rawClass ? '' : ' juco-review__chip--empty'}`}
              >
                {row.rawClass || 'No class'}
              </span>
              {/*
                Names one picker, so it is a real label — but the row already reads the player's
                name aloud beside it, and repeating it visibly would triple the width of a
                40-row list. Hidden visually, present for a screen reader.
              */}
              <label className="juco-review__sr-only" htmlFor={rowSelectId}>
                JC year for {row.label}
              </label>
              <SelectPopover
                id={rowSelectId}
                testId={`${idPrefix}-juco-row-${row.index}`}
                options={JUCO_YEAR_LABELS}
                value={jucoYearLabel(year)}
                onChange={(label) => onChangeRow(row.index, jucoYearFromLabel(label))}
                placeholder="Needs JC year"
                disabled={disabled}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
